import LmsEditorHeader from '../../components/LmsEditorHeader';
import { PageLoader } from '../../components/PageLoader';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Loader2,
  Users,
  UserPlus,
  Shield,
  Calendar,
  AlertCircle,
  Search,
  X,
  Check,
} from 'lucide-react';
import { lmsAPI, rolesAPI, employeesAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { formatDate } from '../../utils/dateHelpers';

interface Assignment {
  id: string;
  module_id: string;
  employee_code: string | null;
  role_id: string | null;
  employee_name: string | null;
  role_name: string | null;
  due_date: string | null;
  is_mandatory: boolean;
  assigned_by: string;
  created_at: string;
}

interface Role {
  id: string;
  name: string;
  category: string;
}

interface Employee {
  empCode?: string;
  emp_code?: string;
  employee_code?: string;
  empName?: string;
  emp_name?: string;
  employeeName?: string;
  name?: string;
}

interface Module {
  id: string;
  title: string;
  description: string | null;
}

type TabType = 'employees' | 'roles';

/**
 * Assignments editor for a module OR a journey.
 *
 * Both are assigned through the same lms_module_assignments table and the same
 * employee/role picker; only the endpoints and the labels differ. `target`
 * comes from the route so /lms/content/:id/assignments and
 * /lms/journeys/:id/assignments share one implementation.
 */
interface LmsAssignmentsPageProps {
  target?: 'module' | 'journey' | 'session';
}

export default function LmsModuleAssignmentsPage({ target = 'module' }: LmsAssignmentsPageProps) {
  const { id: moduleId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;
  const isJourney = target === 'journey';
  const isSession = target === 'session';
  const backPath = isJourney ? '/lms/journeys' : isSession ? '/lms/sessions' : '/lms/content';
  const targetLabel = isJourney ? 'Journey' : isSession ? 'Session' : 'Module';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [module, setModule] = useState<Module | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('employees');

  // Assignment form state
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  // Fetch module and assignments
  const fetchData = useCallback(async () => {
    if (!moduleId || !projectId) return;

    try {
      setLoading(true);

      // Fetch the module/journey being assigned, plus its existing assignments
      if (isJourney) {
        const journeyResponse = await lmsAPI.getJourney(moduleId, projectId);
        setModule(journeyResponse.data?.data?.journey || null);
        const assignmentsResponse = await lmsAPI.getJourneyAssignments(moduleId, projectId);
        setAssignments(assignmentsResponse.data?.data || []);
      } else if (isSession) {
        const sessionResponse = await lmsAPI.getSession(moduleId, projectId);
        setModule(sessionResponse.data?.data || null);
        const assignmentsResponse = await lmsAPI.getSessionAssignments(moduleId, projectId);
        setAssignments(assignmentsResponse.data?.data || []);
      } else {
        const moduleResponse = await lmsAPI.getModuleById(moduleId, projectId);
        setModule(moduleResponse.data?.data?.module || null);
        const assignmentsResponse = await lmsAPI.getModuleAssignments(moduleId, projectId);
        setAssignments(assignmentsResponse.data?.data || []);
      }

      // Fetch roles for the project
      const rolesResponse = await rolesAPI.getByProject(projectId);
      setRoles(rolesResponse.data?.data || []);

      // Fetch employees for the project
      const employeesResponse = await employeesAPI.getProjectEmployees(true, projectId);
      // The API returns { data: { deployed, undeployed, all } }, we need the 'all' array
      const employeesData = employeesResponse.data?.data?.all || employeesResponse.data?.data || [];
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [moduleId, projectId, isJourney, isSession]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssign = async () => {
    if (!moduleId || !projectId) return;

    const assignmentsToCreate: Array<{
      type: 'employee' | 'role';
      employee_code?: string;
      role_id?: string;
      due_date: string | null;
      is_mandatory: boolean;
    }> = [];

    if (activeTab === 'employees') {
      selectedEmployees.forEach((empCode) => {
        assignmentsToCreate.push({
          type: 'employee',
          employee_code: empCode,
          due_date: dueDate || null,
          is_mandatory: isMandatory,
        });
      });
    } else {
      selectedRoles.forEach((roleId) => {
        assignmentsToCreate.push({
          type: 'role',
          role_id: roleId,
          due_date: dueDate || null,
          is_mandatory: isMandatory,
        });
      });
    }

    if (assignmentsToCreate.length === 0) {
      alert('Please select at least one employee or role');
      return;
    }

    try {
      setSaving(true);
      if (isJourney) {
        await lmsAPI.createJourneyAssignments(moduleId, assignmentsToCreate, projectId);
      } else if (isSession) {
        await lmsAPI.createSessionAssignments(moduleId, assignmentsToCreate, projectId);
      } else {
        await lmsAPI.createAssignments(moduleId, assignmentsToCreate, projectId);
      }

      // Reset form and refresh
      setSelectedEmployees([]);
      setSelectedRoles([]);
      setDueDate('');
      setIsMandatory(false);
      fetchData();
    } catch (err) {
      console.error('Error creating assignments:', err);
      alert('Failed to create assignments');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to remove this assignment?')) return;

    try {
      await lmsAPI.deleteAssignment(assignmentId, projectId);
      fetchData();
    } catch (err) {
      console.error('Error deleting assignment:', err);
      alert('Failed to delete assignment');
    }
  };

  const toggleEmployee = (empCode: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(empCode)
        ? prev.filter((c) => c !== empCode)
        : [...prev, empCode]
    );
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((r) => r !== roleId)
        : [...prev, roleId]
    );
  };

  // Helper to get employee code from various field names
  const getEmpCode = (emp: Employee) => emp.empCode || emp.emp_code || emp.employee_code || '';
  const getEmpName = (emp: Employee) => emp.empName || emp.emp_name || emp.employeeName || emp.name || '';

  const filteredEmployees = employees.filter((emp) => {
    const search = employeeSearch.toLowerCase();
    const code = getEmpCode(emp);
    const name = getEmpName(emp);
    return (
      name?.toLowerCase().includes(search) ||
      code?.toLowerCase().includes(search)
    );
  });

  // Filter out already assigned employees/roles
  const alreadyAssignedEmployees = new Set(
    assignments.filter((a) => a.employee_code).map((a) => a.employee_code)
  );
  const alreadyAssignedRoles = new Set(
    assignments.filter((a) => a.role_id).map((a) => a.role_id)
  );

  const availableEmployees = filteredEmployees.filter(
    (emp) => !alreadyAssignedEmployees.has(getEmpCode(emp))
  );
  const availableRoles = roles.filter(
    (role) => !alreadyAssignedRoles.has(role.id)
  );

  // Bulk selection. The employee dropdown only renders the first 50 matches to
  // stay responsive on a full project roster, so these deliberately act on
  // every match rather than the visible slice — otherwise "select all" would
  // quietly assign 50 people out of several hundred.
  const availableEmployeeCodes = availableEmployees.map(getEmpCode).filter(Boolean);
  const allEmployeesSelected =
    availableEmployeeCodes.length > 0 &&
    availableEmployeeCodes.every((code) => selectedEmployees.includes(code));
  const allRolesSelected =
    availableRoles.length > 0 && availableRoles.every((role) => selectedRoles.includes(role.id));

  const toggleAllEmployees = () => {
    setSelectedEmployees((prev) => {
      if (allEmployeesSelected) {
        const drop = new Set(availableEmployeeCodes);
        return prev.filter((code) => !drop.has(code));
      }
      return Array.from(new Set([...prev, ...availableEmployeeCodes]));
    });
  };

  const toggleAllRoles = () => {
    setSelectedRoles((prev) => {
      const ids = availableRoles.map((role) => role.id);
      if (allRolesSelected) {
        const drop = new Set(ids);
        return prev.filter((id) => !drop.has(id));
      }
      return Array.from(new Set([...prev, ...ids]));
    });
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <LmsEditorHeader
        onBack={() => navigate(backPath)}
        title="Manage Assignments"
        subtitle={<>{module?.title || targetLabel} - Assign to employees or roles</>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assignment Form */}
        <div className="rounded-lg border border-gray-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Assignments</h2>

          {/* Tabs */}
          <div className="flex mb-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('employees')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'employees'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserPlus className="w-4 h-4 inline-block mr-2" />
              Employees
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'roles'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Shield className="w-4 h-4 inline-block mr-2" />
              Roles
            </button>
          </div>

          {/* Employee Selection */}
          {activeTab === 'employees' && (
            <div className="space-y-4">
              <div className="relative">
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Employees
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllEmployees}
                    disabled={availableEmployeeCodes.length === 0}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400"
                  >
                    {allEmployeesSelected
                      ? `Clear all ${availableEmployeeCodes.length}`
                      : `Select all ${availableEmployeeCodes.length}${employeeSearch.trim() ? ' matching' : ''}`}
                  </button>
                </div>
                <div
                  className="relative"
                  onFocus={() => setShowEmployeeDropdown(true)}
                >
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => {
                      setEmployeeSearch(e.target.value);
                      setShowEmployeeDropdown(true);
                    }}
                    onFocus={() => setShowEmployeeDropdown(true)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Search employees..."
                  />
                </div>

                {showEmployeeDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowEmployeeDropdown(false)}
                    />
                    <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                      {availableEmployees.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No employees available
                        </div>
                      ) : (
                        availableEmployees.slice(0, 50).map((emp) => {
                          const code = getEmpCode(emp);
                          const name = getEmpName(emp);
                          return (
                            <button
                              key={code}
                              onClick={() => toggleEmployee(code)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                            >
                              <span>
                                {name}{' '}
                                <span className="text-gray-400">({code})</span>
                              </span>
                              {selectedEmployees.includes(code) && (
                                <Check className="w-4 h-4 text-primary-600" />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Selected employees chips */}
              {selectedEmployees.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedEmployees.map((empCode) => {
                    const emp = employees.find((e) => getEmpCode(e) === empCode);
                    return (
                      <span
                        key={empCode}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                      >
                        {emp ? getEmpName(emp) : empCode}
                        <button
                          onClick={() => toggleEmployee(empCode)}
                          className="hover:text-primary-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Role Selection */}
          {activeTab === 'roles' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Select Roles</label>
                <button
                  type="button"
                  onClick={toggleAllRoles}
                  disabled={availableRoles.length === 0}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400"
                >
                  {allRolesSelected
                    ? `Clear all ${availableRoles.length}`
                    : `Select all ${availableRoles.length}`}
                </button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableRoles.length === 0 ? (
                  <p className="text-sm text-gray-500">No roles available</p>
                ) : (
                  availableRoles.map((role) => (
                    <label
                      key={role.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedRoles.includes(role.id)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{role.name}</p>
                        <p className="text-xs text-gray-500">Category: {role.category}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Due Date and Mandatory */}
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline-block mr-1" />
                Due Date (optional)
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isMandatory}
                onChange={(e) => setIsMandatory(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">
                <AlertCircle className="w-4 h-4 inline-block mr-1 text-orange-500" />
                Mark as Mandatory
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleAssign}
            disabled={
              saving ||
              (activeTab === 'employees' && selectedEmployees.length === 0) ||
              (activeTab === 'roles' && selectedRoles.length === 0)
            }
            className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {saving ? 'Assigning...' : 'Assign'}
          </button>
        </div>

        {/* Current Assignments */}
        <div className="rounded-lg border border-gray-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Current Assignments ({assignments.length})
          </h2>

          {assignments.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500">No assignments yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Assign employees or roles to this {targetLabel.toLowerCase()}
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        assignment.role_id
                          ? 'bg-purple-100 text-purple-600'
                          : 'bg-blue-100 text-blue-600'
                      }`}
                    >
                      {assignment.role_id ? (
                        <Shield className="w-4 h-4" />
                      ) : (
                        <UserPlus className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {assignment.role_name || assignment.employee_name || assignment.employee_code}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{assignment.role_id ? 'Role' : 'Employee'}</span>
                        {assignment.due_date && (
                          <>
                            <span>|</span>
                            <span>Due: {formatDate(assignment.due_date)}</span>
                          </>
                        )}
                        {assignment.is_mandatory && (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                            Mandatory
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteAssignment(assignment.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
